import { FC, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportNumberOfPaymentsByBillingPlanData,
} from '@src/types/reports';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useProducts } from '@src/hooks/useProducts';
import {
  getNumberOfPaymentsByBillingPlan,
  exportNumberOfPaymentsByBillingPlan,
} from '@src/services/reports';
import { buildCommonPayloadWithGroupingAndProducts } from '@src/utils/buildCommonPayload';
import { downloadFile } from '@src/lib/utils';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    name: string;
  }>;
  label?: string;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card-stat p-2.5 border border-[#3D3D3D] rounded shadow-lg">
        <p className="font-semibold">{`Date: ${label}`}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

export const NumberOfPaymentsByBillingPlan: FC = () => {
  const [data, setData] = useState<TReportNumberOfPaymentsByBillingPlanData[]>(
    []
  );
  const [availableProducts, setAvailableProducts] = useState<string[]>([]);

  const { products } = useProducts();

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableProducts: true,
    enableComparisons: true,
  });

  const {
    selectedPeriod,
    setSelectedPeriod,
    periodRange,
    valueStart,
    setValueStart,
    selectedGroupBy,
    setSelectedGroupBy,
    selectedProducts,
    setSelectedProducts,
    compareToPreviousPeriod,
    setCompareToPreviousPeriod,
    compareToSamePeriodPreviousYear,
    setCompareToSamePeriodPreviousYear,
    showPreviousPeriodCheckbox,
    showPreviousYearCheckbox,
    isLoading,
    setIsLoading,
    startDate,
    endDate,
  } = filters;

  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: availableProducts,
    compareToPreviousPeriod,
    compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products[key] || 0,
    getPreviousPeriodField: (item, key) => item.products_previous?.[key],
    getPreviousYearField: (item, key) => item.products_previous_year?.[key],
    itemsField: 'products',
    includeTotalsRow: true,
  });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod,
    compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products[key] || 0,
    getPreviousPeriodField: (item, key) => item.products_previous?.[key],
    getPreviousYearField: (item, key) => item.products_previous_year?.[key],
  });

  const buildPayload = () => {
    const base = buildCommonPayloadWithGroupingAndProducts(filters);

    return {
      ...base,
      start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
      end_date: endDate
        ? dayjs(endDate).add(1, 'day').format('YYYY-MM-DD')
        : null,
    };
  };

  const handleRunReport = async () => {
    setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getNumberOfPaymentsByBillingPlan(payload);

      setData(response.data);
      setAvailableProducts(response.available_products || []);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error fetching report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildPayload();
      const response = await exportNumberOfPaymentsByBillingPlan(payload);

      downloadFile(response.data, 'number-of-payments-by-billing-plan');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Number of Payments by Billing Plan
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={compareToSamePeriodPreviousYear}
          dateRangeValue={valueStart}
          groupByOptions={groupByOptions}
          isLoading={isLoading}
          periodOptions={periodOptions}
          periodRange={periodRange}
          productOptions={products}
          selectedGroupBy={selectedGroupBy}
          selectedPeriod={selectedPeriod}
          selectedProducts={selectedProducts}
          showCompareToPreviousPeriod={showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={showPreviousYearCheckbox}
          showProductsFilter={true}
          onCompareToPreviousPeriodChange={setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={setValueStart}
          onGroupByChange={setSelectedGroupBy}
          onPeriodChange={setSelectedPeriod}
          onProductsChange={setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis stroke="#999" style={{ fontSize: '12px' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((item, index) => (
                <Line
                  key={item}
                  activeDot={{ r: 6 }}
                  dataKey={item}
                  dot={{ fill: getChartColor(index), r: 4 }}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {/* Previous period lines */}
              {compareToPreviousPeriod &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_period`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {/* Previous year lines */}
              {compareToSamePeriodPreviousYear &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_year`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download XLSX
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
